import { useMfa, validateExpirationDate } from '@iheartjane/menu';
import type {
  ButtonProps,
  FlexProps,
  TextFieldProps,
} from '@iheartjane/reefer';
import {
  Box,
  Button,
  Flex,
  Link,
  Typography,
  dateRegex,
  telRegex,
} from '@iheartjane/reefer';
import { Form, FormFieldWrapper } from '@iheartjane/reefer-hook-form';
import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect } from 'react';
import { isChrome } from 'react-device-detect';
import type { UseFormReturn } from 'react-hook-form';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import type { CheckoutFormFields } from '@jane/kiosk/providers';
import { validateBirthdateField } from '@jane/kiosk/util';
import type { Store } from '@jane/shared/types';
import { formatPhone } from '@jane/shared/util';

import { MultifactorVerificationInput } from '../MultifactorAuthentication/MultifactorVerificationInput';

import styles from './CustomerInfoFields.module.css';

export type CheckoutFormFieldsDevice = {
  'b-day': number;
  'checkout-notes'?: string;
  code?: string;
  'contact-num': string;
  'name-f': string;
  'name-l': string;
  'name-li': string;
};

type CustomerFieldProps = {
  /**
   * Note: `input` elements support an `autocomplete` attribute, to signal to the browser that it should not
   * prompt to auto-fill suggestions (like previously-used search terms, or email logins, or passwords from
   * any supported password managers). However, browsers have mixed support for this attribute, and can entirely
   * ignore a suggestion not to autocomplete ('autocomplete=off').
   * Knowing that the kiosk will generally run on a public device and that no credentials should be saved, we can
   * further trick browsers like Chrome to honor our autocomplete hint. For example, setting
   * 'autocomplete=new-password' additionally prevents Chrome from prompting to use the Chrome password manager.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: TextFieldProps['autocomplete'];
  defaultValue?: string;
  labelHidden?: boolean;
  readOnly?: boolean;
};

type BirthDateFieldProps = CustomerFieldProps & {
  store: Store;
};

type InnerCustomerFieldProps = {
  defaultValues?: CheckoutFormFields;
  formMethods: UseFormReturn<CheckoutFormFieldsDevice>;
  handleSubmit: () => void;
};

type BaseCustomerFormProps = PropsWithChildren<InnerCustomerFieldProps>;

const getNameSuffix = (autoComplete?: TextFieldProps['autocomplete']) =>
  autoComplete === 'new-password' || autoComplete === 'off' ? '-search' : '';

export const BaseCustomerForm = ({
  children,
  defaultValues,
  formMethods,
  handleSubmit,
}: BaseCustomerFormProps) => {
  // When data loads, reset the form with new default values
  useEffect(() => {
    if (defaultValues && formMethods) {
      formMethods.reset(defaultValues);
    }
  }, [defaultValues, formMethods]);

  return (
    <Form.BaseForm
      formMethods={formMethods}
      name="Cart User Details"
      onSubmit={() => handleSubmit()}
    >
      {children}
    </Form.BaseForm>
  );
};

const FieldWrapper = ({
  children,
  label,
  labelHidden,
  width,
}: PropsWithChildren<{
  label: ReactNode;
  labelHidden?: boolean;
  width?: FlexProps['width'];
}>) => {
  const { getKioskSpacing } = useKioskSpacing();
  return (
    <Flex flexDirection="column" width={width}>
      {!labelHidden && (
        <Typography mb={getKioskSpacing(16, 12)} variant="body-semibold">
          {label}
        </Typography>
      )}
      {children}
    </Flex>
  );
};

export const FirstNameField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  readOnly,
  labelHidden,
}: CustomerFieldProps) => (
  <FieldWrapper label="First name" labelHidden={labelHidden} width="100%">
    <Form.TextField
      autocomplete={autoComplete}
      autoFocus
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Please enter your first name."
      label="First name"
      labelHidden
      name={`name-f${getNameSuffix(autoComplete)}`}
      pattern={/^\w+/}
      placeholder="First name"
      readOnly={readOnly}
      required
      data-testid="first-name-field"
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const LastNameField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  readOnly,
  labelHidden,
}: CustomerFieldProps) => (
  <FieldWrapper label="Last name" labelHidden={labelHidden} width="100%">
    <Form.TextField
      autocomplete={autoComplete}
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Please enter your last name."
      label="Last name"
      labelHidden
      name={`name-l${getNameSuffix(autoComplete)}`}
      pattern={/^\w+/}
      placeholder="Last name"
      readOnly={readOnly}
      required
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const LastInitialField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  readOnly,
  labelHidden,
}: CustomerFieldProps) => (
  <FieldWrapper label="Last initial" labelHidden={labelHidden} width="100%">
    <Form.TextField
      autocomplete={autoComplete}
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Please enter your last initial."
      label="Last initial"
      labelHidden
      name={`name-li${getNameSuffix(autoComplete)}`}
      maxLength={1}
      placeholder="Last initial"
      readOnly={readOnly}
      required
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const PhoneNumberField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  readOnly,
  labelHidden,
}: CustomerFieldProps) => (
  <FieldWrapper label="Phone number" labelHidden={labelHidden} width="100%">
    <Form.NumberField
      allowLeadingZeros
      autocomplete={autoComplete}
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Double-check that your phone number has 10 digits."
      label="Phone number"
      labelHidden
      maskFormat="(###) ###-####"
      name={`contact-num${getNameSuffix(autoComplete)}`}
      pattern={telRegex}
      placeholder="123-456-7890"
      readOnly={readOnly}
      required
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const BirthDateField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  readOnly,
  labelHidden,
  store,
}: BirthDateFieldProps) => (
  <FieldWrapper label="Birth date" labelHidden={labelHidden} width="100%">
    <Form.NumberField
      allowLeadingZeros
      autocomplete={autoComplete}
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Please enter your birth date."
      label="Birth date"
      labelHidden
      maskFormat="##/##/####"
      name={`b-day${getNameSuffix(autoComplete)}`}
      pattern={dateRegex}
      placeholder="MM/DD/YYYY"
      readOnly={readOnly}
      required
      validate={(value: string) => validateBirthdateField(value, store)}
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const IDNumberField = () => (
  <FieldWrapper label="ID number" width="100%">
    <Form.TextField
      disableMobileInputStyling
      enterKeyHint="go"
      label="ID number"
      labelHidden
      maxLength={100}
      name="id-number"
      placeholder="ID number"
      required
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const ExpirationDateField = () => (
  <FieldWrapper label="Expiration date" width="100%">
    <Form.NumberField
      allowLeadingZeros
      disableMobileInputStyling
      enterKeyHint="go"
      errorMessage="Please enter a valid expiration date."
      label="Expiration date"
      labelHidden
      maskFormat="##/##/####"
      name="expiration-date"
      pattern={dateRegex}
      placeholder="MM/DD/YYYY"
      required
      validate={(value: string) => validateExpirationDate(value)}
      shouldUnregister={false}
    />
  </FieldWrapper>
);

export const NotesField = ({
  autoComplete = isChrome ? 'new-password' : 'off',
  labelHidden,
}: CustomerFieldProps) => (
  <FieldWrapper
    label={
      <>
        Note{' '}
        <Typography as="span" color="grays-mid">
          (Optional)
        </Typography>
      </>
    }
    labelHidden={labelHidden}
    width="100%"
  >
    <Form.TextAreaField
      autocomplete={autoComplete}
      className={styles['notesField']}
      disableMobileInputStyling
      label="Note (optional)"
      labelHidden
      name="checkout-notes"
      placeholder="Add any specific notes related to your order."
      data-testid="checkout-notes"
    />
  </FieldWrapper>
);

export const MFAField = ({
  codeResent,
  errorSendingCode,
  errorVerifyingCode,
  phoneNumber,
  resendCode,
  sendingCode,
}: {
  codeResent: boolean;
  errorSendingCode: string | null;
  errorVerifyingCode: string | null;
  phoneNumber: string;
  resendCode: () => void;
  sendingCode: boolean;
}) => {
  const { canResendCode } = useMfa();

  const showResendButton = canResendCode();

  return (
    <Box>
      <Typography mb={16}>
        Enter the code sent to phone number {formatPhone(phoneNumber)} to verify
        your identity and complete your checkout.
      </Typography>
      <Flex
        mb={24}
        width="100%"
        justifyContent="center"
        flexDirection="column"
        gap={24}
      >
        <FormFieldWrapper
          defaultValue=""
          name="code"
          required
          minLength={6}
          render={({ field }) => (
            <MultifactorVerificationInput field={field} atCheckout />
          )}
        />
        {showResendButton && (
          <Link onClick={() => resendCode()}>Send code again</Link>
        )}

        {!showResendButton && !sendingCode && (
          <Typography>
            Didn't receive a code? Try another payment method or contact
            support@iheartjane.com
          </Typography>
        )}
      </Flex>

      {errorVerifyingCode && (
        <Typography color="error">{errorVerifyingCode}</Typography>
      )}

      {errorSendingCode && (
        <Typography color="error">
          Something went wrong. Please{' '}
          {!codeResent ? 'resend the code and' : ''} try again or try another
          payment method
        </Typography>
      )}
    </Box>
  );
};

export const CheckoutFlowButton = ({
  ariaLabel,
  buttonLabel,
  disabled,
  loading,
  variant = 'primary',
  ...buttonProps
}: ButtonProps & { buttonLabel: string }) => {
  return (
    <Button
      ariaLabel={ariaLabel}
      disabled={loading || disabled}
      full
      label={
        buttonProps.size !== 'large' ? (
          buttonLabel
        ) : (
          // text-info is the on-primary color; other variants sit on a light fill.
          <Typography
            as="span"
            color={variant === 'primary' ? 'text-info' : undefined}
            variant="h4-semibold"
          >
            {buttonLabel}
          </Typography>
        )
      }
      loading={loading}
      variant={variant}
      {...buttonProps}
    />
  );
};

export const SubmitCustomerInfoButton = ({
  disabled,
  loading,
  ...buttonProps
}: ButtonProps) => {
  return (
    <CheckoutFlowButton
      ariaLabel="submit customer information"
      data-testid="submit customer information"
      disabled={loading || disabled}
      buttonLabel="Place order"
      loading={loading}
      {...buttonProps}
    />
  );
};
